import React, { useCallback, useEffect, useState, useMemo } from "react";
import { StaticRouter } from "react-router-dom/server";
import { Fiche } from "../Fiche/Fiche";
import { Button } from "../Button/Button";
import { Loading } from "./Loading/Loading";
import { FilterGSportkampen } from "./FilterGSportkampen/FilterGSportkampen";
import { debounce } from "lodash";

/**
 * FicheLijst component specifically for G-sportkampen with dynamic filter support.
 * Key differences from FicheLijst:
 * - No popup/detail view (external links only)
 * - Dynamic filter updates: when a filter changes, we call GetFilterDynamic to recalculate options
 * - Controlled filter dropdowns for proper reset behavior
 */
export const FicheLijstGSportkampen = (props) => {
  const apiUrl = "/umbraco/api/";

  // State
  const [ficheData, setFicheData] = useState({
    folderId: null,
    fiches: [],
    type: "ficheGSportkamp",
    totalCount: 0,
    filter: null,
    culture: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [rendered, setRendered] = useState(0);

  // Loading handlers
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);

  // Load fiches with debounce
  const loadFicheData = useCallback(
    debounce((data) => {
      const dataToSend = { ...data, fiches: [] };

      fetch(`${apiUrl}Fiche/GetFiches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend),
      })
        .then((response) => response.json())
        .then((responseData) => {
          if (responseData) {
            if (responseData.filter.page === 0) {
              // First page: replace all fiches
              setFicheData((prev) => ({
                ...prev,
                fiches: responseData.fiches,
                totalCount: responseData.totalCount,
                type: responseData.type || "ficheGSportkamp",
              }));
            } else {
              // Subsequent pages: append fiches
              setFicheData((prev) => ({
                ...prev,
                fiches: [...prev.fiches, ...responseData.fiches],
                totalCount: responseData.totalCount,
              }));
            }
            stopLoading();
          }
        })
        .catch((error) => {
          console.error("Error loading fiches:", error);
          stopLoading();
        });
    }, 500),
    []
  );

  // Load dynamic filter options
  const loadDynamicFilterData = useCallback(
    debounce((data) => {
      fetch(`${apiUrl}Fiche/GetFilterDynamic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
        .then((response) => response.json())
        .then((filterData) => {
          if (filterData) {
            setFicheData((prev) => ({
              ...prev,
              filter: filterData,
            }));
          }
        })
        .catch((error) => {
          console.error("Error loading dynamic filter:", error);
        });
    }, 300),
    []
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (property, value) => {
      startLoading();

      // Update the filter value locally first
      setFicheData((prev) => {
        const newFilter = { ...prev.filter };
        newFilter.page = 0; // Reset to first page
        
        // Update the specific filter value
        const filterToUpdate = newFilter.filters?.find(
          (f) => f.property === property
        );
        if (filterToUpdate) {
          filterToUpdate.value = value;
        }

        // Prepare data for dynamic filter call
        const dataForDynamic = {
          folderId: prev.folderId,
          culture: prev.culture,
          filter: newFilter,
        };

        // Call GetFilterDynamic to recalculate options
        loadDynamicFilterData(dataForDynamic);

        return { ...prev, filter: newFilter };
      });
    },
    [loadDynamicFilterData]
  );

  // Handle load more
  const handleLoadMore = useCallback(() => {
    setFicheData((prev) => {
      const newFilter = { ...prev.filter };
      newFilter.page = (newFilter.page || 0) + 1;
      return { ...prev, filter: newFilter };
    });
  }, []);

  // Initial load - get dynamic filter data
  useEffect(() => {
    if (rendered === 0) {
      const initialData = {
        folderId: props.folderId,
        culture: props.culture,
        filter: {
          page: 0,
          filters: [],
        },
      };

      // Load initial dynamic filter
      fetch(`${apiUrl}Fiche/GetFilterDynamic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(initialData),
      })
        .then((response) => response.json())
        .then((filterData) => {
          setFicheData({
            folderId: props.folderId,
            culture: props.culture,
            fiches: [],
            type: "ficheGSportkamp",
            totalCount: 0,
            filter: filterData,
          });
        })
        .catch((error) => {
          console.error("Error loading initial filter:", error);
        });

      setRendered(1);
    }
  }, [rendered, props.folderId, props.culture]);

  // When filter changes, reload fiches
  useEffect(() => {
    if (ficheData.filter && ficheData.folderId) {
      loadFicheData(ficheData);
    }
  }, [JSON.stringify(ficheData.filter), ficheData.folderId, loadFicheData]);

  return (
    <StaticRouter>
      <div
        data-aos="fade-up"
        className={`fiche-lijst ficheGSportkamp ${
          ficheData.filter?.amountPerRow === 4
            ? "c-fichelijst-col-3"
            : ficheData.filter?.amountPerRow === 5
            ? "c-fichelijst-col-2"
            : ""
        } ${props.bottomSpacing || ""}`}
      >
        <div>
          {/* Filter header */}
          <FilterGSportkampen
            filter={ficheData.filter}
            amount={ficheData.totalCount}
            dictionary={props.dictionary}
            onChange={handleFilterChange}
          />

          {/* Fiches grid */}
          {!isLoading ? (
            <FichesGrid
              dictionary={props.dictionary}
              ficheData={ficheData}
              onLoadMore={handleLoadMore}
            />
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </StaticRouter>
  );
};

// Separated fiches grid component
const FichesGrid = ({ dictionary, ficheData, onLoadMore }) => {
  const fiches = useMemo(() => {
    if (!ficheData?.fiches?.length) {
      return <h5>{dictionary?.notFound || "Geen resultaten gevonden"}</h5>;
    }

    return ficheData.fiches.map((fiche, index) => (
      <Fiche
        key={`Fiche_gsport_${index}`}
        data={fiche}
        ficheType={ficheData.type}
        dictionary={dictionary}
        // No openPopup needed - external links only
      />
    ));
  }, [ficheData.fiches, ficheData.type, dictionary]);

  const showLoadMore =
    Array.isArray(fiches) && fiches.length < ficheData.totalCount;

  return (
    <div className="c-section__body">
      <div>
        <div className="c-fiche-wrap u-clear">{fiches}</div>
        {showLoadMore && (
          <div className="status ng-scope">
            <div className="buttonContainer">
              <Button
                buttonType="secondary"
                label={dictionary?.loadMore || "Meer laden"}
                onClick={onLoadMore}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
